import { Flex, Modal, Popover, Typography } from "antd";
import {
  ReadMoreBtn,
  StyledAbsoluteText,
  StyledBorderedRowBox,
  StyledDoughnutWrapper,
  StyledLightText,
  StyledNoDataMessage,
  SubHeader,
} from "./style";
import { Doughnut } from "react-chartjs-2";
import CartweelImage from "../../assets/images/cartweel.svg";
import { formatNumber } from "../../utils/utils";
import { useAppDispatch, useAppSelector } from "../../store/config";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getUserStart } from "../../store/features/user/user-slice";
import { useTranslation } from "react-i18next";

export const PredictedIncome = (props: {
  revenue: number;
  projectedRevenue: number;
}) => {
  const { revenue, projectedRevenue } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id: trainerId } = useParams<{ id: string }>();
  const [openReadMore, setOpenReadMore] = useState<boolean>(false);

  const { user: authUser } = useAppSelector((state) => state.auth);
  const { user } = useAppSelector((state) => state.user);

  const createdAt = new Date(user?.createdAt || Date.now());
  const today = new Date();

  const isSameMonth =
    createdAt.getMonth() === today.getMonth() &&
    createdAt.getFullYear() === today.getFullYear();

  const renewalDate = new Date(
    today.getFullYear(),
    isSameMonth ? today.getMonth() + 1 : today.getMonth(),
    7
  );

  const hasData = projectedRevenue > 0 && revenue > 0;
  const firstWeekOrNewTrainerWithData = today < renewalDate;

  const diff = projectedRevenue > revenue ? projectedRevenue - revenue : 0;

  useEffect(() => {
    const userId = trainerId || authUser?._id;

    userId && dispatch(getUserStart({ userId }));
  }, [authUser, trainerId]);

  const doughnutData = {
    labels: [t("Estimated Current Income"), t("Estimated Expected Income")],
    datasets: [
      {
        data: [revenue, diff],
        backgroundColor: ["#5F5DF0", "#94A5F2"],
        hoverBackgroundColor: ["#5250c9", "#8999df"],
        borderWidth: 0,
      },
    ],
  };

  const doughnutOptions = {
    plugins: {
      legend: {
        position: "bottom" as "bottom",
        align: "center" as "center",
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          borderRadius: 50,
          usePointStyle: true,
          padding: 20,
        },
        rtl: true,
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem: any) {
            const label = tooltipItem.label || "";
            const value =
              label === t("Estimated Expected Income")
                ? projectedRevenue
                : tooltipItem.raw;
            return ` ${value.toLocaleString()}`;
          },
        },
      },
    },
    direction: "rtl" as "rtl",
  };

  const formattedRenewalDate = `${renewalDate
    .getDate()
    .toString()
    .padStart(2, "0")}.${(renewalDate.getMonth() + 1)
    .toString()
    .padStart(2, "0")}.${renewalDate.getFullYear().toString().slice(-2)}`;

  return (
    <StyledBorderedRowBox>
      <Flex vertical gap={15} style={{ width: "40%" }}>
        <Typography.Title level={5}>{t("Estimated Income")}</Typography.Title>
        {hasData ? (
          <>
            <StyledLightText>
              <SubHeader>הכנסה חודשית משוערת – איך זה עובד?</SubHeader>
              <p>
                הסכום שאתם רואים כאן הוא הערכה משוערת של ההכנסה החודשית שלכם,
                שמתעדכנת בזמן אמת על בסיס פעילות המתאמנים בתכנים שלכם.
              </p>
              <p>
                ההכנסות באפליקציה מחושבות לפי צריכת תוכן בפועל – כלומר, כמה זמן
                מתאמנים צפו בתכנים שלכם מתוך סך הזמן שבילו באפליקציה במהלך
                החודש.
              </p>
              <ReadMoreBtn onClick={() => setOpenReadMore(true)}>
                קרא עוד
              </ReadMoreBtn>
            </StyledLightText>

            {firstWeekOrNewTrainerWithData && (
              <StyledLightText>
                {today < renewalDate && (
                  <b style={{ display: "block" }}>
                    {t("Data Will Update On")}: {formattedRenewalDate}.
                  </b>
                )}
              </StyledLightText>
            )}
          </>
        ) : (
          <StyledLightText>
            <p>
              כאן יופיע בעתיד הסכום המשוער של ההכנסה החודשית שלכם- נתון שמתעדכן
              בזמן אמת בהתאם לפעילות של המתאמנים בתכנים שלכם.
            </p>
            <p>
              כרגע עדיין אין נתונים להצגה, ברגע שיתחילו לצרוך את התכנים שלכם,
              הדשבורד יתחיל להתמלא.
            </p>
            <ReadMoreBtn onClick={() => setOpenReadMore(true)}>
              קרא עוד
            </ReadMoreBtn>
          </StyledLightText>
        )}
      </Flex>

      <ReadMoreModal
        openReadMore={openReadMore}
        setOpenReadMore={setOpenReadMore}
        hasData={hasData}
      />

      <StyledDoughnutWrapper>
        {hasData && !firstWeekOrNewTrainerWithData ? (
          <>
            <Doughnut data={doughnutData} options={doughnutOptions} />
            <StyledAbsoluteText>
              % {formatNumber((revenue / projectedRevenue) * 100)}
            </StyledAbsoluteText>
          </>
        ) : (
          <Popover
            autoAdjustOverflow={false}
            content={
              <StyledNoDataMessage>
                <Flex align="end" gap={10}>
                  {t("Hi")},
                  <img src={CartweelImage} alt="cartweel-man" width={46} />
                </Flex>
                <>{t("No Data Available At The Moment")}.</>
              </StyledNoDataMessage>
            }
            open={true}
          ></Popover>
        )}
      </StyledDoughnutWrapper>
    </StyledBorderedRowBox>
  );
};

const ReadMoreModal = ({
  openReadMore,
  setOpenReadMore,
  hasData = false,
}: {
  openReadMore: boolean;
  setOpenReadMore: (bool: boolean) => void;
  hasData?: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Modal
      title={t("Estimated Income")}
      open={openReadMore}
      onCancel={() => setOpenReadMore(false)}
      width={600}
      footer={null}
      centered
      style={{ direction: "rtl" }}
    >
      {hasData ? (
        <StyledLightText>
          <SubHeader>הכנסה חודשית משוערת – איך זה עובד?</SubHeader>
          <p>
            הסכום שאתם רואים כאן הוא הערכה משוערת של ההכנסה החודשית שלכם,
            שמתעדכנת בזמן אמת על בסיס פעילות המתאמנים בתכנים שלכם.
          </p>
          <p>
            ההכנסות באפליקציה מחושבות לפי צריכת תוכן בפועל – כלומר, כמה זמן
            מתאמנים צפו בתכנים שלכם מתוך סך הזמן שבילו באפליקציה במהלך החודש.
          </p>
          <p>
            מכיוון שהשימוש של המתאמנים משתנה מדי יום, גם ההכנסה המשוערת שלכם
            יכולה להשתנות בהתאם – ולכן מדובר בנתון דינמי.
          </p>
          <p>
            הסכום הסופי והמדויק ננעל ומחושב בסוף כל חודש, לאחר שקלול כל נתוני
            הצפייה בפועל.
          </p>
          <SubHeader>איך אפשר להשפיע על ההכנסה שלכם?</SubHeader>
          <br />
          ככל שיותר מתאמנים צופים בתכנים שלכם ומשתמשים בתוכניות האימון שלכם – כך
          החלק שלכם מתוך קופת התגמול החודשית גדל.
          <br />
          כדאי להמשיך ליצור תוכן איכותי, לעדכן תוכניות ולעודד את המתאמנים להיות
          פעילים – ההשפעה בידיים שלכם
        </StyledLightText>
      ) : (
        <StyledLightText>
          <p>
            כאן יופיע בעתיד הסכום המשוער של ההכנסה החודשית שלכם- נתון שמתעדכן
            בזמן אמת בהתאם לפעילות של המתאמנים בתכנים שלכם.
          </p>
          <p>
            כרגע עדיין אין נתונים להצגה, ברגע שיתחילו לצרוך את התכנים שלכם,
            הדשבורד יתחיל להתמלא.
          </p>
          <p>
            ההכנסות באפליקציה מחושבות לפי צריכה בפועל: ככל שיותר מתאמנים צופים
            בתכנים ובתוכניות האימון שלכם - כך החלק שלכם מתוך קופת התגמול החודשית
            גדל.
          </p>
          <p>
            זה בדיוק הזמן להתחיל להעלות תכנים איכותיים, לשווק ולהזמין את
            המתאמנים להיכנס ולהתנסות – כי ההכנסה באפליקציה מושפעת באופן ישיר
            מהפעולות שלכם.
          </p>
        </StyledLightText>
      )}
    </Modal>
  );
};
